import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  getReglaCobro, createReglaCobro, updateReglaCobro,
} from "../../controllers/api_billing";
import { getMonedas } from "../../controllers/api_monedas";
import { decryptText } from "../../controllers/api";

export default function FormBillingRules() {
  const navigate = useNavigate();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const hashId = qs.get("id");
  let editId = null;
  try { editId = hashId ? Number(decryptText(decodeURIComponent(hashId))) : null; }
  catch { editId = null; }

  const [form, setForm] = useState({
    nombre: "",
    volumen: 0,
    regla: "por_factura",
    monto: "0",
    moneda_id: "",
    estado: true,
  });
  const [monedas, setMonedas] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const mons = await getMonedas({ activo: true });
        const arr = Array.isArray(mons) ? mons : (mons?.data || []);
        setMonedas(arr);
        if (!editId && arr.length > 0) {
          // default a VES si existe, sino la primera.
          const ves = arr.find((m) => (m.codigo || "").toUpperCase() === "VES");
          setForm((f) => ({ ...f, moneda_id: (ves || arr[0]).id }));
        }
      } catch (e) {
        toast.error("Error al cargar monedas.");
      }
      if (editId) {
        try {
          const r = await getReglaCobro(editId);
          setForm({
            nombre: r.nombre || "",
            volumen: r.volumen || 0,
            regla: r.regla || "por_factura",
            monto: String(r.monto ?? "0"),
            moneda_id: r.moneda_id,
            estado: !!r.estado,
          });
        } catch {
          toast.error("Error al cargar la regla.");
        }
      }
    };
    init();
  }, [editId]);

  const validar = () => {
    if (!form.nombre || !form.nombre.trim()) return "El nombre es obligatorio.";
    const v = Number(form.volumen);
    if (Number.isNaN(v) || v < 0) return "Volumen debe ser >= 0.";
    if (!["lote", "por_factura"].includes(form.regla)) return "Regla invalida.";
    const m = Number(form.monto);
    if (Number.isNaN(m) || m < 0) return "Monto debe ser >= 0.";
    if (!form.moneda_id) return "Seleccione una moneda.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validar();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const body = {
        nombre: form.nombre.trim(),
        volumen: Number(form.volumen),
        regla: form.regla,
        monto: Number(form.monto),
        moneda_id: Number(form.moneda_id),
        estado: !!form.estado,
      };
      if (editId) {
        await updateReglaCobro(editId, body);
        toast.success("Regla actualizada.");
      } else {
        await createReglaCobro(body);
        toast.success("Regla creada.");
      }
      setTimeout(() => navigate("/billing-rules"), 800);
    } catch (e) {
      const msg = e?.response?.data?.mensaje || e?.response?.data?.error || "Error al guardar.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full">
      <ToastContainer />
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
        <div className="flex justify-between items-center border-b pb-4 mb-6">
          <h6 className="text-blueGray-700 text-xl font-bold">
            {editId ? "Editar regla de cobro" : "Nueva regla de cobro"}
          </h6>
          <button
            onClick={() => navigate("/billing-rules")}
            className="text-blueGray-500 hover:text-blueGray-700 text-sm"
          ><i className="fas fa-arrow-left mr-1"></i> Volver</button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-blueGray-600 mb-1">Nombre</label>
            <input
              type="text"
              className="border rounded w-full px-3 py-2"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })}
              placeholder='Ej. "Volumen < 100"'
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-blueGray-600 mb-1">
                Volumen umbral (mínimo de facturas para aplicar)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                className="border rounded w-full px-3 py-2"
                value={form.volumen}
                onChange={(e) => setForm({ ...form, volumen: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-blueGray-600 mb-1">Tipo de regla</label>
              <select
                className="border rounded w-full px-3 py-2"
                value={form.regla}
                onChange={(e) => setForm({ ...form, regla: e.target.value })}
              >
                <option value="por_factura">Por factura (monto × volumen)</option>
                <option value="lote">Lote (monto fijo)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-blueGray-600 mb-1">Monto</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="border rounded w-full px-3 py-2"
                value={form.monto}
                onChange={(e) => setForm({ ...form, monto: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-blueGray-600 mb-1">Moneda</label>
              <select
                className="border rounded w-full px-3 py-2"
                value={form.moneda_id || ""}
                onChange={(e) => setForm({ ...form, moneda_id: e.target.value })}
              >
                <option value="">- Seleccione -</option>
                {monedas.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.codigo} — {m.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="estado-chk"
              className="form-checkbox"
              checked={!!form.estado}
              onChange={(e) => setForm({ ...form, estado: e.target.checked })}
            />
            <label htmlFor="estado-chk" className="text-sm font-semibold">Regla activa</label>
          </div>
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="submit"
              disabled={loading}
              className="bg-sirius-600 hover:bg-sirius-700 text-white font-semibold py-2 px-6 rounded shadow disabled:opacity-50"
              style={{ background: "#3039AC" }}
            >
              {loading ? "Guardando..." : (editId ? "Actualizar" : "Crear")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/billing-rules")}
              className="bg-blueGray-200 hover:bg-blueGray-300 text-blueGray-700 font-semibold py-2 px-6 rounded"
            >Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
}
